/**
 * Coup de pouce — Attendu 1052 « comparer deux fractions : 2/7 et 5/7 ;
 * 8/12 et 8/21 ; 3/4 et 7/18 ; 8/3 et 6/7 ». QUATRE pages STATIQUES, une
 * par cas, partagées par les deux generator_keys. Écrite à l'étape 5 de
 * revue-gen contre la doctrine (une page, le cas discriminant), validée par
 * le prof le 2026-09-08, puis étendue aux quatre cas le 2026-09-09 : les
 * deux cartes mélangent les cas, l'élève choisit sa page par les puces
 * nommées du sheet (« Même dénominateur », « Même numérateur »,
 * « Repère 1/2 », « Repère 1 »), les noms des puces sont eux-mêmes le
 * premier geste de l'automatisme (quel cas ?).
 *
 * L'image que l'élève a déjà : la bande de papier partagée en parts égales
 * (CM1, 621), deux bandes de même longueur l'une sous l'autre (les deux
 * droites alignées de 1051). Même dessin sur les quatre pages, cinq
 * éléments, deux couleurs :
 *   1. deux bandes de même longueur, la bande du haut en orange, celle du
 *      bas en bleu, la fraction écrite à droite de chacune ;
 *   2. le repère quand il y en a un : un trait pointillé « 1/2 » (page 3)
 *      ou le trait « 1 » sur des bandes qui vont jusqu'à 2 (page 4) ;
 *   3. le résultat en gros (dernière ligne du calcul, en couleur, sans
 *      commentaire : la flèche « c'est là » a été retirée partout le
 *      2026-09-09, épreuve de Musk) ;
 *   4. la règle, cause puis effet, en deux lignes de 55 caractères au plus.
 * Mots : parts, dénominateur, numérateur, moitié, milieu ; jamais
 * « dénominateur commun », jamais de lettre.
 *
 * Page 4 : 8/3 (l'exemple du programme) est hors domaine du gen (les
 * fractions du cas « un » restent sous 2) ; le couple 4/3 et 5/6 est pris à
 * sa place, discriminant (la plus grande a le plus petit numérateur).
 *
 * COUPLES SACRIFIÉS : les quatre couples des pages, 2/7 et 5/7, 8/12 et
 * 8/21, 2/5 et 5/8, 4/3 et 5/6, ne sortent jamais ensemble d'une carte
 * (gen_1052, auto-testé par cdp1052.self.test.js). Largeur bornée à 340 px,
 * tailles de 1048.
 */

const ORANGE = '#EA580C';
const ORANGE_CLAIR = '#FED7AA';
const BLEU = '#2563EB';
const BLEU_CLAIR = '#DBEAFE';
const ENCRE = '#1E293B';
const GRIS = '#94A3B8';

/** Les bandes : de 0 à `fin` sur 280 px, 30 px de haut. */
const XB = 40;
const LB = 280;
const HB = 30;
const Y1 = 58;
const Y2 = 118;
const XM = XB + LB / 2;

/** Une bande de `fin` unités, chaque unité partagée en `b` parts, les `a` premières coloriées. */
const bande = (y, a, b, fin, fond, bord) => Array.from({ length: b * fin }, (_, i) => {
    const x = XB + (i * LB) / (b * fin);
    const w = LB / (b * fin);
    return `<rect x="${x}" y="${y}" width="${w}" height="${HB}" fill="${i < a ? fond : '#FFFFFF'}" stroke="${i < a ? bord : GRIS}" stroke-width="${i < a ? 1.6 : 1}"/>`;
}).join('\n  ');

/** Une fraction empilée, une couleur. */
const frac = (x, y, n, d, couleur, taille = 14, demi = 10) => `<text x="${x}" y="${y - 4}" font-size="${taille}" font-weight="bold" text-anchor="middle" fill="${couleur}">${n}</text>
  <line x1="${x - demi}" y1="${y}" x2="${x + demi}" y2="${y}" stroke="${couleur}" stroke-width="1.8"/>
  <text x="${x}" y="${y + taille + 1}" font-size="${taille}" font-weight="bold" text-anchor="middle" fill="${couleur}">${d}</text>`;

const gras = (texte, couleur) => `<tspan font-weight="bold" fill="${couleur}">${texte}</tspan>`;

/**
 * Une page : la bande du haut (orange) et la bande du bas (bleu), le repère
 * éventuel, le résultat « haut sym bas », deux lignes de règle.
 */
export const figure = ({ haut, bas, fin, repere, sym, regles, aria }) => {
    const graduations = fin === 1
        ? `<text x="${XB}" y="${Y2 + HB + 22}" font-size="12" font-weight="bold" text-anchor="middle" fill="${ENCRE}">0</text>
  <text x="${XB + LB}" y="${Y2 + HB + 22}" font-size="12" font-weight="bold" text-anchor="middle" fill="${ENCRE}">1</text>`
        : `<text x="${XB}" y="${Y2 + HB + 22}" font-size="12" font-weight="bold" text-anchor="middle" fill="${ENCRE}">0</text>
  <text x="${XM}" y="${Y2 + HB + 22}" font-size="12" font-weight="bold" text-anchor="middle" fill="${ENCRE}">1</text>
  <text x="${XB + LB}" y="${Y2 + HB + 22}" font-size="12" font-weight="bold" text-anchor="middle" fill="${ENCRE}">2</text>`;
    const trait = repere === 'moitie'
        ? `${frac(XM, 30, 1, 2, ENCRE, 11, 8)}
  <line x1="${XM}" y1="${Y1 - 8}" x2="${XM}" y2="${Y2 + HB + 8}" stroke="${ENCRE}" stroke-width="1.6" stroke-dasharray="4 3"/>`
        : (repere === 'un'
            ? `<text x="${XM}" y="${Y1 - 14}" font-size="14" font-weight="bold" text-anchor="middle" fill="${ENCRE}">1</text>
  <line x1="${XM}" y1="${Y1 - 8}" x2="${XM}" y2="${Y2 + HB + 8}" stroke="${ENCRE}" stroke-width="2.4"/>`
            : '');
    return `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 292" width="100%" style="max-width:340px;height:auto;display:block" role="img"
     aria-label="${aria}">
  <!-- le repère, qui traverse les deux bandes -->
  ${trait}

  <!-- bande du haut -->
  ${bande(Y1, haut.a, haut.b, fin, ORANGE_CLAIR, ORANGE)}
  ${frac(XB + LB + 30, Y1 + HB / 2, haut.a, haut.b, ORANGE)}

  <!-- bande du bas -->
  ${bande(Y2, bas.a, bas.b, fin, BLEU_CLAIR, BLEU)}
  ${frac(XB + LB + 30, Y2 + HB / 2, bas.a, bas.b, BLEU)}
  ${graduations}

  <!-- le résultat -->
  ${frac(150, 214, haut.a, haut.b, ORANGE, 17, 12)}
  <text x="185" y="220" font-size="20" font-weight="bold" text-anchor="middle" fill="${ENCRE}">${sym}</text>
  ${frac(220, 214, bas.a, bas.b, BLEU, 17, 12)}

  <!-- la règle, cause puis effet -->
  <text x="200" y="262" font-size="12" text-anchor="middle" fill="${ENCRE}">${regles[0]}</text>
  <text x="200" y="280" font-size="12" text-anchor="middle" fill="${ENCRE}">${regles[1]}</text>
</svg>`.trim();
};

const SVG_MEME_DEN = figure({
    haut: { a: 2, b: 7 }, bas: { a: 5, b: 7 }, fin: 1, repere: null, sym: '&lt;',
    regles: [
        'Même dénominateur : des parts de la même taille.',
        `${gras('5 parts', BLEU)}, c'est plus que ${gras('2 parts', ORANGE)}.`,
    ],
    aria: 'Deux bandes de même longueur, de 0 à 1, l\'une sous l\'autre. Bande du haut partagée en 7 parts, 2 coloriées : 2 sur 7. Bande du bas partagée en 7 parts, 5 coloriées : 5 sur 7. 2 sur 7 est plus petit que 5 sur 7. Même dénominateur : des parts de la même taille. 5 parts, c\'est plus que 2 parts.',
});

export const SVG_MEME_NUM = figure({
    haut: { a: 8, b: 12 }, bas: { a: 8, b: 21 }, fin: 1, repere: null, sym: '&gt;',
    regles: [
        'Même numérateur : 8 parts, mais pas de la même taille.',
        `${gras('En 21', BLEU)}, la part est plus petite qu'${gras('en 12', ORANGE)}.`,
    ],
    aria: 'Deux bandes de même longueur, de 0 à 1, l\'une sous l\'autre. Bande du haut partagée en 12 parts, 8 coloriées : 8 sur 12. Bande du bas partagée en 21 parts, 8 coloriées : 8 sur 21. 8 sur 12 est plus grand que 8 sur 21. Même numérateur : 8 parts, mais pas de la même taille. En 21, la part est plus petite qu\'en 12.',
});

export const SVG_MOITIE = figure({
    haut: { a: 2, b: 5 }, bas: { a: 5, b: 8 }, fin: 1, repere: 'moitie', sym: '&lt;',
    regles: [
        `${gras('Sous le milieu', ORANGE)} : 2 est moins que la moitié de 5.`,
        `${gras('Au-dessus', BLEU)} : 5 est plus que la moitié de 8.`,
    ],
    aria: 'Deux bandes de même longueur, de 0 à 1, l\'une sous l\'autre, avec un trait au milieu marqué un demi. Bande du haut partagée en 5 parts, 2 coloriées : 2 sur 5. Bande du bas partagée en 8 parts, 5 coloriées : 5 sur 8. 2 sur 5 est plus petit que 5 sur 8. Sous le milieu : 2 est moins que la moitié de 5. Au-dessus : 5 est plus que la moitié de 8.',
});

export const SVG_UN = figure({
    haut: { a: 4, b: 3 }, bas: { a: 5, b: 6 }, fin: 2, repere: 'un', sym: '&gt;',
    regles: [
        `${gras('Au-dessus de 1', ORANGE)} : 3 tiers font 1, la 4e part dépasse.`,
        `${gras('En dessous', BLEU)} : il faut 6 sixièmes pour 1, il y en a 5.`,
    ],
    aria: 'Deux bandes de même longueur, de 0 à 2, l\'une sous l\'autre, avec un trait épais au milieu marqué 1. Bande du haut : chaque unité partagée en 3 parts, 4 coloriées : 4 sur 3, ça dépasse 1. Bande du bas : chaque unité partagée en 6 parts, 5 coloriées : 5 sur 6, ça n\'arrive pas à 1. 4 sur 3 est plus grand que 5 sur 6. Au-dessus de 1 : 3 tiers font 1, la quatrième part dépasse. En dessous : il faut 6 sixièmes pour 1, il y en a 5.',
});

const AIDE = {
    pages: [
        { titre: 'Même dénominateur', svg: SVG_MEME_DEN },
        { titre: 'Même numérateur', svg: SVG_MEME_NUM },
        { titre: 'Repère 1/2', svg: SVG_MOITIE },
        { titre: 'Repère 1', svg: SVG_UN },
    ],
};

const CP_1052 = {
    flash_texte_trou_saisie: AIDE,
    plus_qcm_unique: AIDE,
    plus_tableau_vf: AIDE,
};

export default CP_1052;
